import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { ProgressBar } from 'react-bootstrap';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle,
  faFileImport,
} from '@fortawesome/pro-solid-svg-icons';
import Tooltip from 'components/tooltip';
import { useHistory } from 'react-router-dom';

import { getImportRequest } from '../store/selectors';
import SendSupportModal from '../send_support_modal';
import { getLabels, getProgress, getVerificationErrors, getVerificationState } from '../store/verification_selectors';
import { VERIFICATION_STATE } from '../constants';

import './verification.scss';

export function Verification({
  errors,
  importRequest,
  labels,
  progress,
  redirect,
  verificationState
}) {
  const history = useHistory();
  const [showSendSupportModal, setShowSendSupportModal] = useState(false);

  const idStateFail = () => {
    return verificationState === VERIFICATION_STATE.FAIL;
  };

  const handleReload = () => {
    location.reload();
  };

  const handleSendSupport = () => {
    setShowSendSupportModal(true);
  };

  const isStatusFail = () => {
    return verificationState === VERIFICATION_STATE.FAIL || verificationState === VERIFICATION_STATE.PREPROCESS_FAIL;
  };

  useEffect(() => {
    if (verificationState === VERIFICATION_STATE.SUCCESS) {
      setTimeout(() => {
        history.push(redirect);
      }, 2000);
    }
  }, [verificationState]);

  const renderErrorBanner = () => (
    <div className="error-handled-form" name="verificationErrors">
      <div className="error-banner alert alert-danger p-2 pb-4 pt-4 mt-4">
        <div className="danger-icon">
          <div className="icon-container">
            <FontAwesomeIcon icon={faExclamationTriangle} size="3x" />
          </div>
        </div>
        <div className="error-content align-middle">
          <h4 className="text-smaller pl-3">
            We were unable to verify your data for the following reasons:
          </h4>
          <ul className="pl-5">
            <div className="js-contact-role-error">
              {
                errors.map((error, index) => (
                  <li key={index}>{error.message} - {error.file}</li>
                ))
              }
            </div>
          </ul>
        </div>
      </div>
    </div>
  );

  const renderCustomTagsButtons = () => {
    switch (verificationState) {
    case VERIFICATION_STATE.FAIL:
      return <div className="btn btn-dark btn-sm mr-2" onClick={handleReload}>Reload</div>;
    case VERIFICATION_STATE.PREPROCESS_FAIL: {
      const sendTooltipMessage = 'Send this import request to Builtout\'s Data Team to take over the import process.' +
        'A member of the team will be on touch via email with any questions,' +
        'additional requirements, and on import completion.';
      return (
        <div>
          <button className="btn btn-dark btn-sm mr-2" type="button" onClick={handleReload}>Try Again</button>
          <Tooltip
            content={sendTooltipMessage}
            overlayClassName="tooltip-popover-dark"
            placement="top"
          >
            <button className="btn btn-outline-dark btn-sm" type="button" onClick={handleSendSupport}>
              Send to Support
            </button>
          </Tooltip>
        </div>
      );
    }
    default:
      return null;
    }
  };

  const renderProgressBar = () => {
    switch (verificationState) {
    case VERIFICATION_STATE.IN_PROGRESS:
      return <ProgressBar label={`${progress}%`} now={progress} />;
    case VERIFICATION_STATE.SUCCESS:
      return <ProgressBar bsStyle="success" now={100} />;
    case VERIFICATION_STATE.FAIL:
    case VERIFICATION_STATE.PREPROCESS_FAIL:
      return <ProgressBar bsStyle="danger" label="Unable to verify" now={100} />;
    default:
      return null;
    }
  };

  return (
    <div className="white mb-4">
      <h1 name="header-verification-message">{labels.header} <strong>{labels.headerHighlight}</strong></h1>
      <div className={classNames('card-container', { 'card-container-fail': idStateFail() })}>
        <div className="card-title">
          <span>
            <FontAwesomeIcon icon={faFileImport} />
          </span>
          <h2>{importRequest.name}</h2>
        </div>
        {renderProgressBar()}
        <div className="mb-4" name="in-progress-verification-message">
          {labels.progressing}
        </div>
        <div>
          {renderCustomTagsButtons()}
        </div>
      </div>
      <p name="footer-verification-message">{labels.footer}</p>
      {isStatusFail() && renderErrorBanner()}
      <SendSupportModal
        shown={showSendSupportModal}
        onClose={setShowSendSupportModal}
      />
    </div>
  );
}

Verification.propTypes = {
  errors: PropTypes.array.isRequired,
  importRequest: PropTypes.object.isRequired,
  labels: PropTypes.object.isRequired,
  progress: PropTypes.number.isRequired,
  redirect: PropTypes.string.isRequired,
  verificationState: PropTypes.string.isRequired
};

const mapStateToProps = state => ({
  errors: getVerificationErrors(state),
  importRequest: getImportRequest(state),
  labels: getLabels(state),
  progress: getProgress(state),
  verificationState: getVerificationState(state)
});

export default connect(mapStateToProps, null)(Verification);
